// src/domains/revenue/consignor/pages/sent-list-page.jsx
import React, { useEffect, useState } from "react";
import financeApi from "api/finance-api";

function UpdatePayoutModal({ open, onClose, payoutId, onSuccess }) {
  const [status, setStatus] = useState("");
  const [scheduledOn, setScheduledOn] = useState("");
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentKind, setPaymentKind] = useState("");
  const [proofKey, setProofKey] = useState("");
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = !!payoutId && (!!status || !!scheduledOn);

  useEffect(() => {
    if (open) {
      setStatus(""); setScheduledOn(""); setNote("");
      setAmount(""); setPaymentKind(""); setProofKey("");
      setForce(false); setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if ((status === "paying" || status === "paid") && (amount || paymentKind || proofKey)) {
        await financeApi.payouts.confirmPayment({
          id: payoutId,
          amount: amount ? Number(amount) : undefined,
          payment_kind: paymentKind || undefined,
          proof_key: proofKey || undefined,
        });
      } else {
        await financeApi.payouts.update({
          id: payoutId,
          status: status || undefined,
          scheduled_on: scheduledOn || undefined,
          note: note || undefined,
          force: force ? true : undefined,
        });
      }
      onSuccess?.();
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded shadow-lg w-full max-w-xl p-4">
        <h3 className="text-lg font-semibold mb-3">Update Payout #{payoutId}</h3>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-sm mb-1">Status</label>
            <select className="border rounded px-2 py-1 w-full" value={status} onChange={(e)=>setStatus(e.target.value)}>
              <option value="">-- no change --</option>
              <option value="draft">Draft (force only)</option>
              <option value="ready">Ready</option>
              <option value="paying">Paying (use with amount/paymentKind)</option>
              <option value="paid">Paid (use with amount/paymentKind)</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Scheduled On (optional)</label>
            <input type="datetime-local" className="border rounded px-2 py-1 w-full"
                   value={scheduledOn} onChange={(e)=>setScheduledOn(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm mb-1">Note (optional)</label>
            <input className="border rounded px-2 py-1 w-full" value={note} onChange={(e)=>setNote(e.target.value)} />
          </div>

          {(status === "paying" || status === "paid") && (
            <>
              <div>
                <label className="block text-sm mb-1">Amount</label>
                <input type="number" className="border rounded px-2 py-1 w-full"
                       value={amount} onChange={(e)=>setAmount(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm mb-1">Payment Kind</label>
                <input className="border rounded px-2 py-1 w-full"
                       placeholder="transfer / cash / etc."
                       value={paymentKind} onChange={(e)=>setPaymentKind(e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm mb-1">Proof Key (optional)</label>
                <input className="border rounded px-2 py-1 w-full"
                       value={proofKey} onChange={(e)=>setProofKey(e.target.value)} />
              </div>
            </>
          )}

          {status === "draft" && (
            <div className="col-span-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={force} onChange={(e)=>setForce(e.target.checked)} />
                Force rollback to draft (use carefully)
              </label>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button className="border rounded px-3 py-1" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="border rounded px-3 py-1"
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsignorSentListPage() {
  // filters
  const [status, setStatus]   = useState("ready");
  const [hjOnly, setHjOnly]   = useState(false);
  const [sku, setSku]         = useState("");
  const [q, setQ]             = useState("");

  // paging/meta
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // data/loading
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);

  const fmtIDR  = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
  const safeTime = (t) => (t ? String(t).slice(0, 19).replace("T", " ") : "-");

  const fetchData = async () => {
    try {
      setLoading(true); setError("");
      const params = { per_item: 1, page, per_page: perPage };
      if (status && status !== "all") params.status = status;
      if (hjOnly)  params.hj_only = 1;
      if (sku)     params.sku     = sku;
      if (q)       params.q       = q;

      const { data: body } = await financeApi.payouts.getPerItem(params);
      const list = body.items || body.data || [];
      const meta = body.meta  || {};
      setRows(Array.isArray(list) ? list : []);
      setTotalPages(Number(meta.total_pages || 1));
      setTotalCount(Number(meta.total_count || list.length || 0));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load data.");
      setRows([]); setTotalPages(1); setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page, perPage]);

  const onApply = () => { setPage(1); fetchData(); };

  const openUpdate = (payoutId) => { setSelectedPayoutId(payoutId); setModalOpen(true); };

  const viewPayout = async (payoutId) => {
    try {
      const { data } = await financeApi.payouts.show({ id: payoutId, hj_only: 1 });
      alert(`Payout #${data?.data?.id}\nStatus: ${data?.data?.status}\nItems: ${data?.data?.payout_items?.length || 0}`);
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Load payout failed");
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">(SA) List of Sent (per SKU)</h1>
        <div className="flex gap-2 items-center flex-wrap">
          <select className="border rounded px-2 py-1"
                  value={status}
                  onChange={(e)=>{ setStatus(e.target.value); setPage(1); }}>
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="ready">Ready</option>
            <option value="paying">Paying</option>
            <option value="paid">Paid</option>
            <option value="canceled">Canceled</option>
          </select>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={hjOnly}
                   onChange={(e)=>{ setHjOnly(e.target.checked); setPage(1); }} />
            HJ only
          </label>

          <input className="border rounded px-2 py-1" placeholder="SKU…"
                 value={sku} onChange={(e)=>setSku(e.target.value)}
                 style={{ width: 160 }} />

          <input className="border rounded px-2 py-1" placeholder="Search seller email/name or SKU…"
                 value={q} onChange={(e)=>setQ(e.target.value)}
                 style={{ width: 280 }} />

          <button className="border rounded px-3 py-1" onClick={onApply} disabled={loading}>
            {loading ? "Loading…" : "Apply"}
          </button>
        </div>
      </div>

      {error && <div className="text-red-600 mb-3">Request failed: {error}</div>}

      <div className="overflow-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Payout</th>
              <th className="text-left px-3 py-2">Seller</th>
              <th className="text-left px-3 py-2">SKU</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">HJ</th>
              <th className="text-left px-3 py-2">Sold At</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((it, idx) => (
              <tr key={it.id || it.item_id || `${it.sku}-${idx}`} className="border-b">
                <td className="px-3 py-2">{(page - 1) * perPage + idx + 1}</td>
                <td className="px-3 py-2">#{it.payout_id}</td>
                <td className="px-3 py-2">{it.user?.name || it.user?.email || (it.user ? `UID:${it.user.id}` : "-")}</td>
                <td className="px-3 py-2">{it.sku}</td>
                <td className="px-3 py-2 text-right">{fmtIDR(it.amount)}</td>
                <td className="px-3 py-2 text-right">{fmtIDR(it.selling_price)}</td>
                <td className="px-3 py-2">{safeTime(it.sold_at)}</td>
                <td className="px-3 py-2">{it.payout_status || "-"}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    {/* <button className="border rounded px-2 py-1" onClick={() => viewPayout(it.payout_id)}>View</button> */}
                    <button className="border rounded px-2 py-1" onClick={() => openUpdate(it.payout_id)}>Update</button>
                  </div>
                </td>
              </tr>
            ))}
            {(!rows || rows.length === 0) && !loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">No data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button className="border rounded px-3 py-1" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1 || loading}>Prev</button>
        <div>Page {page} / {totalPages}</div>
        <button className="border rounded px-3 py-1" onClick={()=>setPage(p=>p+1)} disabled={loading || page>=totalPages}>Next</button>

        <div className="ml-4">
          <label className="mr-2 text-sm">Per page</label>
          <select className="border rounded px-2 py-1" value={perPage}
                  onChange={(e)=>{ setPerPage(parseInt(e.target.value,10)); setPage(1); }}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="ml-auto text-sm text-gray-600">
          Total: {totalCount.toLocaleString("id-ID")}
        </div>
      </div>

      <UpdatePayoutModal
        open={modalOpen}
        payoutId={selectedPayoutId}
        onClose={() => setModalOpen(false)}
        onSuccess={() => fetchData()}
      />
    </div>
  );
}

export { ConsignorSentListPage };
export default ConsignorSentListPage;
